import { useDraggableInputFile } from './Hooks/useDraggableInputFile.inner.tsx';
import PropTypes from 'prop-types';
import * as S from './DragAndDrop.styles.tsx';
import Icon from '../icon/Icon.tsx';
import P from '../texts/p/P.tsx';
import { MIME_TYPES_MAP } from '../../utils/Constants.tsx';
import Card from '../card/Card.tsx';
import { useRef, useState } from 'react';
import Spinner from '../Spinner/spinner.tsx';
import { useClientTheme } from '../../context/ThemeContext';

const DragAndDrop = ({
  name,
  setValue,
  fileTypes,
  maxFileSize,
  size,
  disabled,
  showError,
  formatTextError = 'El formato de archivo solo puede ser: .pdf, .jpg, .jpeg, .png, .xls, .xlsx, .doc, .docx'
}) => {
  const { clientTheme } = useClientTheme();
  const [showErrorMessage] = useDraggableInputFile(
    setValue,
    fileTypes,
    maxFileSize,
    size
  );

  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const supportedTypes = fileTypes.map((type) => MIME_TYPES_MAP[type]);
  const getStatus = () => (!showErrorMessage ? 'normal' : 'error');
  const [fileData, setFileData] = useState({ name: '', size: 0 });
  const fileInputRef = useRef(null);

  const dragOverHandler = (ev) => {
    ev.preventDefault();
  };

  const dropHandler = (ev) => {
    ev.preventDefault();
    if (!disabled) {
      setIsFileLoading(true);
      [...ev.dataTransfer.items].map((dataTransferItem) => {
        if (dataTransferItem.kind === 'file') {
          const file = dataTransferItem.getAsFile();
          if (validFileType(file) && validFileSize(file)) {
            handleFile(file);
            showError('');
          } else {
            if (!validFileType(file)) {
              showError(
                formatTextError
              );
            }
            if (!validFileSize(file)) {
              showError('El tamaño del archivo no puede ser mayor de 20MB');
            }
          }
        }
      });
      setIsFileLoading(false);
      removeDragData(ev);
    }
  };

  const handleInputFileClick = (ev) => {
    setIsFileLoading(true);
    ev.preventDefault();
    const file = fileInputRef.current.files[0];
    if (validFileType(file) && validFileSize(file)) {
      handleFile(file);
      showError('');
    } else {
      if (!validFileType(file)) {
        showError(
          formatTextError
        );
      }
      if (!validFileSize(file)) {
        showError('El tamaño del archivo no puede ser mayor de 10 MB');
      }
    }
    setIsFileLoading(false);
    fileInputRef.current.value = '';
  };

  const handleFile = (file) => {
    setValue(file);
    setFileData({
      name: file.name,
      size: file.size,
    });
    setIsFileLoading(false);
  };

  const validFileType = (file) => {
    if (supportedTypes.includes(file?.type)) {
      return true;
    } else {
      return false;
    }
  };

  const validFileSize = (file: File) => {
    return file.size <= maxFileSize;
  };

  const removeDragData = (ev) => {
    ev.dataTransfer.items?.clear();
    ev.dataTransfer.clearData();
  };

  const handleDivClick = () => {
    fileInputRef.current.click();
  };

  const renderEmptyInput = () => (
    <>
      <S.DragAndDrop
        status={getStatus()}
        onDragOver={dragOverHandler}
        onDrop={dropHandler}
        name={name}
        size={size}
        disabled={disabled}
        onClick={handleDivClick}
        useTheme={!!clientTheme}
      >
        {isFileLoading ? (
          <Spinner size='md' variant='primary' />
        ) : (
          <Card>
            <S.DropBaseIcon size={size} useTheme={!!clientTheme}>
              <S.DropBaseIconWrapper useTheme={!!clientTheme}>
                <Icon
                  name='upload_cloud_outline'
                  variant='neutral'
                  modifier='strong'
                  height={32}
                  width={32}
                />
              </S.DropBaseIconWrapper>
            </S.DropBaseIcon>
            <S.MainTextWrapper size={size}>
              <Card display='flex' gap='4px' justifyContent='center'>
                <P withMargin={false} type='1' variant={clientTheme ? 'primary' : 'success'} weight='bold'>
                  Hacé click para cargar
                </P>
                <P withMargin={false} type='1' variant='neutral'>
                  o arrastrá y soltá
                </P>
              </Card>
              <P withMargin={false} type='2' variant='neutral' align='center'>
                PDF (Peso máximo 20MB)
              </P>
            </S.MainTextWrapper>
          </Card>
        )}
        <S.ActionWrapper size={size}>
          <S.FileInput
            type='file'
            name={name}
            id={name}
            onChange={(e) => handleInputFileClick(e)}
            multiple={true}
            ref={fileInputRef}
            disabled={disabled}
          />
        </S.ActionWrapper>
      </S.DragAndDrop>
    </>
  );

  return <S.DragAndDropWrapper>{renderEmptyInput()}</S.DragAndDropWrapper>;
};

DragAndDrop.defaultProps = {
  size: 'md',
  disabled: false,
};

DragAndDrop.propTypes = {
  name: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  maxFileSize: PropTypes.number.isRequired,
  fileTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(MIME_TYPES_MAP)))
    .isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'auto']),
  disabled: PropTypes.bool,
};

export default DragAndDrop;
